import { InjectionToken } from "@angular/core";

/**
 * interfaces
 */
import {
    IUserInfoService,
    IUserAuthzService,
    IUserPayloadService,
    IUserAuthorizedUnitsService,
    IUserModulePermissionsService,
    IUserRoleControlService,
    IUserInfoControlService,
    IUserInfoModelService,
} from "../interfaces";

/**
 * Injection token for user information service
 * @constant {InjectionToken<IUserInfoService>} USER_INFO
 * @description Token used to inject user information management services.
 * Provides access to user personal data, contact information, and basic profile details.
 * 
 * @example
 * ```typescript
 * constructor(@Inject(USER_INFO) private userInfo: IUserInfoService) {
 *   // Access user information model
 *   const user = this.userInfo.userInfoModel;
 *   console.log('User name:', user?.firstName, user?.lastName);
 * }
 * ```
 */
export const USER_INFO = new InjectionToken<IUserInfoService>('USER_INFO');

/**
 * Injection token for user authorization service
 * @constant {InjectionToken<IUserAuthzService>} USER_AUTHZ
 * @description Token used to inject user authorization and permissions services.
 * Provides access to user roles, facility information, and authorization data.
 * 
 * @example
 * ```typescript
 * constructor(@Inject(USER_AUTHZ) private userAuthz: IUserAuthzService) {
 *   // Access user authorization data
 *   console.log('User role:', this.userAuthz.roleName);
 *   console.log('Facility ID:', this.userAuthz.facilityId);
 * }
 * ```
 */
export const USER_AUTHZ = new InjectionToken<IUserAuthzService>('USER_AUTHZ');

/**
 * Injection token for user payload service
 * @constant {InjectionToken<IUserPayloadService>} USER_PAYLOAD
 * @description Token used to inject user payload data management services.
 * Provides access to complete user payload data from JWT token including permissions and authorized units.
 * 
 * @example
 * ```typescript
 * constructor(@Inject(USER_PAYLOAD) private userPayload: IUserPayloadService) {
 *   // Access complete user payload
 *   const payload = this.userPayload.payload;
 *   console.log('User permissions:', payload?.module_permissions);
 * }
 * ```
 */
export const USER_PAYLOAD = new InjectionToken<IUserPayloadService>('USER_PAYLOAD');

/**
 * Injection token for user authorized units service
 * @constant {InjectionToken<IUserAuthorizedUnitsService>} USER_AUTHORIZED_UNITS
 * @description Token used to inject user authorized units management services.
 * Provides access to list of units the user has authorization to access.
 * 
 * @example
 * ```typescript
 * constructor(@Inject(USER_AUTHORIZED_UNITS) private authorizedUnits: IUserAuthorizedUnitsService) {
 *   // Access authorized units
 *   const units = this.authorizedUnits.authorizedUnits;
 *   console.log('Authorized units:', units);
 * }
 * ```
 */
export const USER_AUTHORIZED_UNITS = new InjectionToken<IUserAuthorizedUnitsService>('USER_AUTHORIZED_UNITS');

/**
 * Injection token for user module permissions service
 * @constant {InjectionToken<IUserModulePermissionsService>} USER_MODULE_PERMISSIONS
 * @description Token used to inject user module permissions management services.
 * Provides access to granular permissions for each application module.
 * 
 * @example
 * ```typescript
 * constructor(@Inject(USER_MODULE_PERMISSIONS) private modulePermissions: IUserModulePermissionsService) {
 *   // Access module permissions
 *   const permissions = this.modulePermissions.modulePermissions;
 *   console.log('Module permissions:', permissions);
 * }
 * ```
 */
export const USER_MODULE_PERMISSIONS = new InjectionToken<IUserModulePermissionsService>('USER_MODULE_PERMISSIONS');

/**
 * Injection token for user authorization control service
 * @constant {InjectionToken<IUserAuthzControlService>} USER_AUTHZ_CONTROL
 * @description Token used to inject user authorization control services.
 * Provides methods for checking user permissions and access levels within modules.
 * 
 * @example
 * ```typescript
 * constructor(@Inject(USER_AUTHZ_CONTROL) private authzControl: IUserAuthzControlService) {
 *   // Check user permissions
 *   if (this.authzControl.hasViewerAccessToModule(ContentPageModules.DASHBOARD)) {
 *     // User can view dashboard
 *   }
 * }
 * ```
 */
export const USER_ROLE_CONTROL = new InjectionToken<IUserRoleControlService>('USER_ROLE_CONTROL');

/**
 * Injection token for user information control service
 * @constant {InjectionToken<IUserInfoControlService>} USER_INFO_CONTROL
 * @description Token used to inject user information control services.
 * Provides methods for setting and clearing user information state.
 * 
 * @example
 * ```typescript
 * constructor(@Inject(USER_INFO_CONTROL) private userInfoControl: IUserInfoControlService) {
 *   // Set user information (DTO from API)
 *   this.userInfoControl.setUserInfo({
 *     id: '123',
 *     email: 'user@example.com',
 *     first_name: 'John',
 *     last_name: 'Doe',
 *     role_id: 'admin',
 *     role_name: 'Administrator',
 *     mfa_factor_id: null
 *   });
 * 
 *   // Clear user information
 *   this.userInfoControl.clearUserInfo();
 * }
 * ```
 */
export const USER_INFO_CONTROL = new InjectionToken<IUserInfoControlService>('USER_INFO_CONTROL');

/**
 * Injection token for user info model service
 * @constant {InjectionToken<IUserInfoModelService>} USER_INFO_MODEL
 * @description Token used to inject a model-mapped view of the current user info.
 * Provides access to the current user information model both synchronously and as observables.
 * 
 * @example
 * ```typescript
 * constructor(@Inject(USER_INFO_MODEL) private userInfoModel: IUserInfoModelService) {
 *   // Access user information
 *   const userInfo = this.userInfoModel.userInfo;
 *   console.log('User information:', userInfo);
 * 
 *   // Subscribe to user information changes
 *   this.userInfoModel.userInfo$.subscribe(userInfo => {
 *     console.log('User information:', userInfo);
 *   });
 * }
 * ```
 */
export const USER_INFO_MODEL = new InjectionToken<IUserInfoModelService>('USER_INFO_MODEL');

